export const DEFAULT_LOCALE = "en";

// Keep in sync with `config.i18n.available_locales` (config/application.rb). An
// unrecognized `?locale=` is ignored rather than trusted: otherwise the frontend
// would initialize vue-i18n with a bogus locale and request a nonexistent
// catalog while the server has already fallen back to English.
const AVAILABLE_LOCALES = new Set(["en", "es", "pt", "ht", "zh-CN", "ar", "fr"]);

// A valid `?locale=` is the primary signal when explicitly passed (it survives
// even when Sparkle content is embedded in a host page whose own <html lang> we
// don't control), followed by <html lang> (set when Sparkle renders the page
// standalone) and finally English.
function localeFromUrl() {
  try {
    const param = new URLSearchParams(window.location.search).get("locale") || "";
    return AVAILABLE_LOCALES.has(param) ? param : "";
  } catch {
    return "";
  }
}

// The server publishes its resolved locale to `window.Sparkle.locale` (see the
// `sparkle_locale_js` Rails helper). When Sparkle is embedded in a host page we
// don't control its `<html lang>` (often a hardcoded "en-US"), so this
// Sparkle-set signal is preferred over `<html lang>` — but still below an
// explicit `?locale=`. Validated like `?locale=` so a bogus value is ignored.
function localeFromSparkle() {
  try {
    const locale = (window.Sparkle && window.Sparkle.locale) || "";
    return AVAILABLE_LOCALES.has(locale) ? locale : "";
  } catch {
    return "";
  }
}

// `<html lang>` is often a regional tag (e.g. "en-US" from an embedding CMS's
// template), so normalize it to an available locale — exact match, else the
// primary language subtag — rather than trusting it verbatim. Otherwise we'd
// initialize vue-i18n under (and request a catalog for) a locale the server
// would ignore.
function localeFromDocument() {
  return normalizeCandidate(document.documentElement.lang);
}

// Exact (case-insensitive) match against the available locales, else the
// candidate's primary language subtag, else "".
function normalizeCandidate(candidate) {
  return normalizeLocale(candidate) || normalizeLocale((candidate || "").split("-")[0]);
}

function normalizeLocale(candidate) {
  if (!candidate) {
    return "";
  }

  const candidateLower = candidate.toLowerCase();
  for (const locale of AVAILABLE_LOCALES) {
    if (locale.toLowerCase() === candidateLower) {
      return locale;
    }
  }

  return "";
}

export function resolveLocale(locale) {
  // The explicit argument is normalized like every other signal — an
  // unrecognized value falls through to the next source instead of
  // initializing vue-i18n under a locale the server would ignore.
  return normalizeCandidate(locale) || localeFromUrl() || localeFromSparkle() || localeFromDocument() || DEFAULT_LOCALE;
}

// The locale to forward on Sparkle-bound requests: the resolved page locale, or
// null for the default so default-language URLs stay param-free and cacheable.
export function requestLocale() {
  const locale = resolveLocale();

  return locale === DEFAULT_LOCALE ? null : locale;
}
